import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarDays, Check, ChevronDown, Loader2, MessagesSquare, Paperclip } from "lucide-react";
import { toast } from "sonner";
import { AppMainHeader } from "@/components/AppMainHeader";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { UserAvatar } from "@/components/UserAvatar";
import { useAuth } from "@/contexts/AuthContext";
import { useChat } from "@/contexts/ChatContext";
import { getTrabajos, updateTrabajo, getApiErrorMessage } from "@/lib/api";
import { TRABAJO_ESTADOS, TRABAJO_ESTADO, formatFechaCorta, fechaVencida, ordenarTrabajos } from "@/lib/clienteNombre";
import { TrabajoDetalleDialog } from "@/components/chat/TrabajoDialogs";
import { ChatTrabajosSwitch } from "@/components/chat/ChatTrabajosSwitch";
import { RichTextContent } from "@/components/RichTextEditor";
import { ClienteLogo } from "@/components/clientes/ClienteLogo";
import { cn } from "@/lib/utils";

const FILTRO_STORAGE_KEY = "trabajos_filtro_persona";

function TarjetaTrabajo({ t, onMover, onAbrir, arrastrable }) {
  const { usuariosByEmail } = useChat();
  const vencida = t.estado !== "hecho" && fechaVencida(t.fecha_limite);
  return (
    <div
      draggable={arrastrable}
      onDragStart={(e) => {
        e.dataTransfer.setData("text/trabajo", t.id);
        e.dataTransfer.effectAllowed = "move";
      }}
      onClick={onAbrir}
      className="group rounded-xl border bg-background p-3 shadow-sm hover:shadow-md transition cursor-pointer active:cursor-grabbing space-y-2.5"
    >
      <div className="flex items-center gap-2 min-w-0">
        <ClienteLogo cliente={t.cliente} size={20} className="rounded-md" />
        <span className="text-[11px] text-muted-foreground truncate flex-1">{t.cliente?.nombre_visible}</span>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button type="button" onClick={(e) => e.stopPropagation()} className="h-6 px-1.5 rounded-md flex items-center gap-1 text-[11px] text-muted-foreground hover:bg-muted" title="Cambiar estado">
              <span className={cn("h-2 w-2 rounded-full", TRABAJO_ESTADO[t.estado]?.dot)} />
              <ChevronDown className="w-3 h-3" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
            {TRABAJO_ESTADOS.map((e) => (
              <DropdownMenuItem key={e.key} onSelect={() => onMover(t, e.key)}>
                <span className={cn("h-2 w-2 rounded-full mr-2", e.dot)} />
                {e.label}
                {t.estado === e.key && <Check className="w-3.5 h-3.5 ml-auto" />}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <p className={cn("text-sm font-medium leading-snug", t.estado === "hecho" && "line-through text-muted-foreground")}>{t.titulo}</p>
      {t.descripcion && <RichTextContent html={t.descripcion} className="text-xs text-muted-foreground line-clamp-2 [&_*]:!my-0" />}
      {(t.fecha_limite || t.asignados?.length > 0 || t.adjuntos?.length > 0) && (
        <div className="flex items-center justify-between gap-2">
          <span className="flex items-center gap-2.5">
            {t.fecha_limite && (
              <span className={cn("flex items-center gap-1 text-[11px] tabular-nums", vencida ? "text-destructive font-medium" : "text-muted-foreground")}>
                <CalendarDays className="w-3 h-3" /> {formatFechaCorta(t.fecha_limite)}
              </span>
            )}
            {t.adjuntos?.length > 0 && (
              <span className="flex items-center gap-0.5 text-[11px] text-muted-foreground">
                <Paperclip className="w-3 h-3" /> {t.adjuntos.length}
              </span>
            )}
          </span>
          <span className="flex -space-x-1.5">
            {(t.asignados || []).slice(0, 4).map((e) => (
              <span key={e} className="rounded-full ring-2 ring-background inline-flex" title={usuariosByEmail[e]?.nombre || e}>
                <UserAvatar user={usuariosByEmail[e] || { email: e }} size={22} />
              </span>
            ))}
          </span>
        </div>
      )}
    </div>
  );
}

export default function MisTrabajosPage() {
  const navigate = useNavigate();
  const { user, authLoading } = useAuth();
  const { usuarios, usuariosByEmail, subscribe } = useChat();
  const [trabajos, setTrabajos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [persona, setPersona] = useState(() => {
    try {
      return localStorage.getItem(FILTRO_STORAGE_KEY) || "yo";
    } catch {
      return "yo";
    }
  });
  const [clienteFiltro, setClienteFiltro] = useState("");
  const [sobreColumna, setSobreColumna] = useState(null);
  const [abiertoId, setAbiertoId] = useState(null);
  const recargaRef = useRef(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) navigate("/login", { replace: true });
    else if (user.permissions?.chat === false) navigate("/candidatos", { replace: true });
  }, [authLoading, user, navigate]);

  const cargar = useCallback(() => {
    return getTrabajos(persona === "todos" ? {} : { asignado: persona })
      .then((d) => setTrabajos(d.trabajos || []))
      .catch(() => toast.error("Error al cargar trabajos"))
      .finally(() => setLoading(false));
  }, [persona]);

  useEffect(() => {
    if (!user || user.permissions?.chat === false) return;
    setLoading(true);
    cargar();
  }, [user, cargar]);

  useEffect(
    () =>
      subscribe((ev) => {
        if (ev.type === "trabajo_actualizado" || ev.type === "trabajo_eliminado" || ev.type === "reconectado") {
          clearTimeout(recargaRef.current);
          recargaRef.current = setTimeout(cargar, 300);
        }
      }),
    [subscribe, cargar]
  );

  const cambiarPersona = (p) => {
    setPersona(p);
    try {
      localStorage.setItem(FILTRO_STORAGE_KEY, p);
    } catch (_) {}
  };

  const mover = async (t, estado) => {
    if (t.estado === estado) return;
    const anterior = t.estado;
    setTrabajos((prev) => prev.map((x) => (x.id === t.id ? { ...x, estado } : x)));
    try {
      await updateTrabajo(t.id, { estado });
    } catch (err) {
      setTrabajos((prev) => prev.map((x) => (x.id === t.id ? { ...x, estado: anterior } : x)));
      toast.error(getApiErrorMessage(err, "No se pudo mover el trabajo"));
    }
  };

  const clientes = useMemo(() => {
    const map = new Map();
    trabajos.forEach((t) => t.cliente && map.set(t.cliente.id, t.cliente));
    return [...map.values()].sort((a, b) => a.nombre_visible.localeCompare(b.nombre_visible));
  }, [trabajos]);

  const visibles = trabajos.filter((t) => !clienteFiltro || t.cliente_id === clienteFiltro);
  const etiquetaPersona = persona === "yo" ? "Mis trabajos" : persona === "todos" ? "Todo el equipo" : usuariosByEmail[persona]?.nombre || persona;
  const clienteSel = clientes.find((c) => c.id === clienteFiltro);

  if (authLoading || !user) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="h-dvh flex flex-col bg-background overflow-hidden">
      <AppMainHeader title="Trabajos" titleAs="h5" />
      <div className="shrink-0 flex items-center gap-2 px-4 md:px-6 py-3 border-b overflow-x-auto">
        <ChatTrabajosSwitch activo="trabajos" className="w-56 shrink-0 mr-2" />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="gap-2 h-10 shrink-0">
              {persona !== "todos" && <UserAvatar user={persona === "yo" ? user : usuariosByEmail[persona] || { email: persona }} size={22} />}
              {etiquetaPersona}
              <ChevronDown className="w-4 h-4 opacity-60" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-60 max-h-[60vh] overflow-y-auto">
            <DropdownMenuItem onSelect={() => cambiarPersona("yo")}>Mis trabajos</DropdownMenuItem>
            <DropdownMenuItem onSelect={() => cambiarPersona("todos")}>Todo el equipo</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">Por persona</DropdownMenuLabel>
            {usuarios
              .filter((u) => u.email !== user.email)
              .map((u) => (
                <DropdownMenuItem key={u.email} onSelect={() => cambiarPersona(u.email)}>
                  <UserAvatar user={u} size={20} className="mr-2" />
                  {u.nombre}
                </DropdownMenuItem>
              ))}
          </DropdownMenuContent>
        </DropdownMenu>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="gap-2 h-10 shrink-0">
              {clienteSel && <ClienteLogo cliente={clienteSel} size={20} className="rounded-md" />}
              {clienteSel ? clienteSel.nombre_visible : "Todos los clientes"}
              <ChevronDown className="w-4 h-4 opacity-60" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-64 max-h-[60vh] overflow-y-auto">
            <DropdownMenuItem onSelect={() => setClienteFiltro("")}>Todos los clientes</DropdownMenuItem>
            <DropdownMenuSeparator />
            {clientes.map((c) => (
              <DropdownMenuItem key={c.id} onSelect={() => setClienteFiltro(c.id)}>
                <ClienteLogo cliente={c} size={20} className="rounded-md mr-2" />
                <span className="truncate">{c.nombre_visible}</span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        <span className="ml-auto hidden lg:block shrink-0 text-[11px] text-muted-foreground">Orden: fecha límite más cercana primero · sin fecha, por antigüedad</span>
      </div>

      {loading ? (
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="flex-1 min-h-0 overflow-x-auto snap-x snap-mandatory md:snap-none">
          <div className="h-full grid grid-cols-[repeat(4,minmax(260px,85vw))] md:grid-cols-[repeat(4,minmax(240px,1fr))] gap-3 p-3 md:p-6">
            {TRABAJO_ESTADOS.map((col) => {
              const items = ordenarTrabajos(visibles.filter((t) => t.estado === col.key));
              return (
                <section
                  key={col.key}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setSobreColumna(col.key);
                  }}
                  onDragLeave={() => setSobreColumna((c) => (c === col.key ? null : c))}
                  onDrop={(e) => {
                    e.preventDefault();
                    setSobreColumna(null);
                    const id = e.dataTransfer.getData("text/trabajo");
                    const t = trabajos.find((x) => x.id === id);
                    if (t) mover(t, col.key);
                  }}
                  className={cn("snap-start flex flex-col min-h-0 rounded-2xl p-2 transition-colors", col.col, sobreColumna === col.key && "ring-2 ring-primary/40")}
                >
                  <header className="flex items-center gap-2 px-2 h-9 shrink-0">
                    <span className={cn("h-2.5 w-2.5 rounded-full", col.dot)} />
                    <h2 className="text-sm font-medium">{col.label}</h2>
                    <span className="text-xs text-muted-foreground tabular-nums">{items.length}</span>
                  </header>
                  <div className="flex-1 min-h-0 overflow-y-auto space-y-2 p-1 mobile-nav-scroll">
                    {items.map((t) => (
                      <TarjetaTrabajo key={t.id} t={t} arrastrable onMover={mover} onAbrir={() => setAbiertoId(t.id)} />
                    ))}
                    {items.length === 0 && <p className="text-xs text-muted-foreground/70 text-center py-6">{col.key === "hecho" ? "Lo terminado en los últimos 30 días aparece aquí" : "Nada por aquí"}</p>}
                  </div>
                </section>
              );
            })}
          </div>
        </div>
      )}
      {!loading && trabajos.length === 0 && (
        <div className="absolute inset-x-0 bottom-28 flex justify-center pointer-events-none">
          <p className="pointer-events-auto text-xs text-muted-foreground bg-background border rounded-full px-4 py-2 shadow-sm flex items-center gap-2">
            <MessagesSquare className="w-3.5 h-3.5" /> Los trabajos se crean desde el chat de cada cliente
          </p>
        </div>
      )}
      <TrabajoDetalleDialog
        trabajo={trabajos.find((t) => t.id === abiertoId) || null}
        onOpenChange={(o) => !o && setAbiertoId(null)}
        miembros={trabajos.find((t) => t.id === abiertoId)?.cliente?.miembros || []}
        cliente={trabajos.find((t) => t.id === abiertoId)?.cliente}
        onSaved={(nuevo) => setTrabajos((prev) => prev.map((x) => (x.id === nuevo.id ? { ...nuevo, cliente: x.cliente } : x)))}
        onDeleted={(id) => setTrabajos((prev) => prev.filter((x) => x.id !== id))}
        onIrAlChat={() => {
          const t = trabajos.find((x) => x.id === abiertoId);
          if (t) navigate(`/chat/${t.cliente_id}`);
        }}
      />
    </div>
  );
}
